import React from 'react';
import { OverlayTrigger, Tooltip, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';

interface SidebarItem {
  key: string;
  label: string;
  icon: any;
}

type SidebarTheme = 'dark' | 'light' | 'grayBordered' | 'shadowWhite';

interface SidebarProps {
  items: SidebarItem[];
  activeKey: string;
  onSelect: (key: string) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  theme?: SidebarTheme;
  width?: number;
  fontSize?: string;
  compact?: boolean;
}

// ✅ Add strong typing for theme style
interface ThemeStyle {
  bgColor: string;
  textColor: string;
  hoverBg: string;
  activeColor: string;
  activeBg: string;
  borderRight?: string;
  boxShadow?: string;
}

// ✅ Add full typing
const themeStyles: Record<SidebarTheme, ThemeStyle> = {
  dark: {
    bgColor: '#1e293b',
    textColor: '#e2e8f0',
    hoverBg: '#475569',
    activeColor: '#10b981',
    activeBg: '#334155'
  },
  light: {
    bgColor: '#f9fafb',
    textColor: '#374151',
    hoverBg: '#e5e7eb',
    activeColor: '#0284c7',
    activeBg: '#e0f2fe'
  },
  grayBordered: {
    bgColor: '#f3f4f6',
    textColor: '#374151',
    hoverBg: '#e5e7eb',
    activeColor: '#0ea5e9',
    activeBg: '#e0f2fe',
    borderRight: '1px solid #d1d5db'
  },
  shadowWhite: {
    bgColor: '#ffffff',
    textColor: '#1f2937',
    hoverBg: '#f3f4f6',
    activeColor: '#2563eb',
    activeBg: '#dbeafe',
    boxShadow: '0 0 6px rgba(0,0,0,0.08)'
  }
};

const Sidebar: React.FC<SidebarProps> = ({
  items,
  activeKey,
  onSelect,
  collapsed,
  onToggleCollapse,
  theme = 'dark',
  width = 200,
  fontSize = '14px',
  compact = false
}) => {
  const collapsedWidth = 80;
  const styles = themeStyles[theme];

  return (
    <div
      className="sidebar d-flex flex-column align-items-start p-2"
      style={{
        width: collapsed ? collapsedWidth : width,
        backgroundColor: styles.bgColor,
        color: styles.textColor,
        transition: 'width 0.3s ease',
        height: '1130px',
        overflowX: 'hidden',
        fontFamily: "'Inter', sans-serif",
        fontSize,
        borderRight: styles.borderRight,
        boxShadow: styles.boxShadow
      }}
    >
      {!compact && (
        <div className="w-100 d-flex justify-content-end mb-3">
          <Button
            variant="link"
            onClick={onToggleCollapse}
            style={{ padding: '4px 6px', color: styles.textColor }}
          >
            <FontAwesomeIcon icon={collapsed ? faChevronRight : faChevronLeft} />
          </Button>
        </div>
      )}

      {items.map((item) => (
        <OverlayTrigger
          key={item.key}
          placement="right"
          overlay={<Tooltip>{item.label}</Tooltip>}
        >
          <div
            className={`sidebar-item mb-2 px-3 py-2 d-flex align-items-center rounded`}
            style={{
              cursor: 'pointer',
              color: activeKey === item.key ? styles.activeColor : styles.textColor,
              fontWeight: activeKey === item.key ? 600 : 400,
              backgroundColor: activeKey === item.key ? styles.activeBg : 'transparent',
              transition: 'all 0.2s ease-in-out',
              width: '100%'
            }}
            onClick={() => onSelect(item.key)}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.transform = 'scale(1.03)';
              (e.currentTarget as HTMLElement).style.backgroundColor = styles.hoverBg;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
              (e.currentTarget as HTMLElement).style.backgroundColor =
                activeKey === item.key ? styles.activeBg : 'transparent';
            }}
          >
            <FontAwesomeIcon icon={item.icon} size="lg" className="me-2" />
            {!collapsed && <span>{item.label}</span>}
          </div>
        </OverlayTrigger>
      ))}
    </div>
  );
};

export default Sidebar;
